import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Alert (inline) — Fastn Design System contract.
 *
 * The DS has no error toast on purpose: an error needs to persist and to answer
 * what / why / next, which a five-second toast cannot do. This is where errors
 * belong.
 *
 *   heading  = what happened
 *   children = why, in plain words
 *   action   = how to fix it
 *
 * Error tone announces once via `role="alert"`; other tones use `role="status"`
 * so they do not interrupt.
 */
const alertVariants = cva(
  cn(
    "flex w-full gap-3 rounded-md border p-4 text-sm",
    "[&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:mt-0.5",
  ),
  {
    variants: {
      tone: {
        info: "border-info/30 bg-info/12 text-fg",
        success: "border-success/30 bg-success/12 text-fg",
        warning: "border-warning/30 bg-warning/12 text-fg",
        error: "border-error/30 bg-error/12 text-fg",
      },
    },
    defaultVariants: { tone: "info" },
  },
);

const DOT_TONE = {
  info: "bg-info",
  success: "bg-success",
  warning: "bg-warning",
  error: "bg-error",
} as const;

export interface AlertProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title">,
    VariantProps<typeof alertVariants> {
  /** What happened. Rendered bold, first. */
  heading?: string;
  /** How to fix it. */
  action?: { label: string; onClick: () => void };
}

export function Alert({
  className,
  tone = "info",
  heading,
  action,
  children,
  ...props
}: AlertProps): React.ReactElement {
  const t = tone ?? "info";
  return (
    <div
      className={cn(alertVariants({ tone: t }), className)}
      role={t === "error" ? "alert" : "status"}
      data-testid={`alert-${t}`}
      {...props}
    >
      {/* Status is carried by the text, never by colour alone — the dot is
          decorative reinforcement. */}
      <span aria-hidden className={cn("mt-1.5 size-1.5 shrink-0 rounded-pill", DOT_TONE[t])} />
      <div className="flex min-w-0 flex-col gap-1">
        {heading ? <p className="font-semibold text-fg">{heading}</p> : null}
        {children ? <div className="text-fg-body">{children}</div> : null}
        {action ? (
          <button
            type="button"
            onClick={action.onClick}
            className="mt-1 self-start text-brand underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            data-testid={`alert-${t}-action-btn`}
          >
            {action.label}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export { alertVariants };
